import type { DrawingEntity } from '../contract/types.js';
import { angleGap } from '../match/candidate.js';
import { readDxf } from '../dxf/reader.js';
import { TEXT_OUTLINE_LAYER } from '../dxf/outline.js';
import { scoreAgainstGroundTruth, type ScoreReport } from '../metrics/score.js';
import { verifyIdentity, type IdentityReport } from '../identity/chain.js';
import {
  GATE_PRECISION,
  GATE_RECALL,
  ROUND_TRIP_GATE_PRECISION,
  ROUND_TRIP_GATE_RECALL,
} from './gate.js';

/**
 * The accuracy pipeline, as two reported stages and the figure they compose to.
 *
 * AIONA's meeting of 2026-08-19 fixed that there are two stages and that both
 * are measured: the vector PDF against `drawing.json`, and `drawing.json`
 * against the DXF. Stage 1 has been reported per entity type since the first
 * build. Stage 2 existed only as a pass/fail Python test, so a reader of the
 * acceptance report could see one of the two numbers AIONA now expects.
 *
 *   stage 1     ground truth  ->  drawing.json     recognition
 *   stage 2     drawing.json  ->  DXF read back    serialisation
 *   end to end  ground truth  ->  DXF read back    what the customer receives
 *
 * **Stage 2 is stated as exactly 1.000, not at or above 0.98.** Two gates at
 * 0.98 in series permit 0.9604 delivered, and both would be green on a file
 * that is 96 % correct. Stage 2 is already exact-or-fail — a round trip either
 * carries the number or it does not — so the stricter wording costs nothing and
 * closes the composition. This is stricter than AIONA asked for and is offered
 * as such.
 *
 * **The end-to-end figure is measured, not multiplied.** Multiplying two
 * per-type ratios assumes the two stages fail independently and on the same
 * entities, and neither is knowable from the two numbers. Ground truth is
 * matched against the DXF read-back directly, which is also the only one of the
 * three that describes what a customer opens.
 */

/**
 * The tolerance stage 2 is measured at, in drawing millimetres.
 *
 * The board's `max(0.2 mm, diagonal x 0.001)` is a *recognition* tolerance:
 * the allowance for finding the shape that is on the page. Writing a number
 * into a file and reading it back has no such allowance — float round trip
 * through DXF is exact, so anything above zero is a transform or serialisation
 * defect rather than accumulated loss. 1e-6 is the bar
 * `kernel/tests/test_round_trip.py` has always used, kept identical so the two
 * implementations are comparable.
 */
export const ROUND_TRIP_TOLERANCE_MM = 1e-6;

/** The same reasoning applied to arc angles, which the board allows 0.5 degrees. */
export const ROUND_TRIP_ANGLE_TOLERANCE_DEG = 1e-6;

/**
 * The two bars, restated from the gate rather than repeated.
 *
 * A threshold written down twice is a threshold that can disagree with itself,
 * and the gate is the one that decides. These exist so a report can print what
 * it was measured against without importing the gate's whole vocabulary.
 */
export const STAGE_2_GATE = {
  precision: ROUND_TRIP_GATE_PRECISION,
  recall: ROUND_TRIP_GATE_RECALL,
} as const;

export const END_TO_END_GATE = { precision: GATE_PRECISION, recall: GATE_RECALL } as const;

export interface ReadBackSummary {
  /** How many entities were read out of the DXF and scored. */
  entities: number;
  /**
   * DXF entity types the reader has no case for, counted by name.
   *
   * Never silently skipped: a reader that ignores what it cannot parse reports
   * a perfect score for a file it only partly read.
   */
  unsupported: Record<string, number>;
  /**
   * Text-outline polylines excluded before scoring.
   *
   * Outlines are the same string drawn a second time as line-work, for the
   * class of reader Finding 8 describes that never reads a font name. They are
   * not drawing entities, they are on their own layer so they can be switched
   * off, and no gate has ever seen them — scoring them would fail a correct
   * file. Counted rather than dropped, because a number the report computes and
   * declines to print is worse than one it never computed.
   */
  outlinesExcluded: number;
}

export interface StageReports {
  stage1: ScoreReport;
  stage2: ScoreReport;
  endToEnd: ScoreReport;
  readBack: ReadBackSummary;
  /**
   * Which entities, rather than how many.
   *
   * Stage 2's figures answer *how many* and cannot answer *which*, and the two
   * come apart on a type that changed in transit: one entity, correct in every
   * coordinate, reported as a miss plus a spurious. This names the faults and
   * where they are.
   */
  identity: IdentityReport;
}

export interface StageInput {
  /** Ground truth, in drawing millimetres. */
  truth: readonly DrawingEntity[];
  /** What extraction produced, in drawing millimetres. */
  produced: readonly DrawingEntity[];
  /** The DXF written from the same document. Empty when the conversion failed. */
  dxf: string;
  /**
   * The document's pages, when the page attribution is to be checked too.
   *
   * Separate from `produced`, which is flattened into drawing space and has lost
   * which sheet each entity came from.
   */
  pages?: readonly (readonly DrawingEntity[])[];
  /**
   * Score stage 1 and the end-to-end figure at an explicit tolerance instead of
   * deriving the board's from the ground truth's own extent.
   *
   * The conversion-failed path passes 0. That fixture's truth is still in PDF
   * points, because the transform that would have moved it into millimetres was
   * never built, and a tolerance derived from that geometry is a page-space
   * number reported in a millimetre column — where it is larger than any real
   * one and quietly becomes the loosest row in the pooled figure. Nothing was
   * produced, so nothing was matched at any tolerance, and zero is the honest
   * number rather than a convenient one.
   *
   * Stage 2 is unaffected: it is always measured at {@link ROUND_TRIP_TOLERANCE_MM}.
   */
  toleranceMm?: number;
}

/** A multi-page document prefixes its layers with the sheet, so match on the suffix. */
function isOutlineLayer(layer: string): boolean {
  return layer === TEXT_OUTLINE_LAYER || layer.endsWith(`_${TEXT_OUTLINE_LAYER}`);
}

/**
 * Whether the DXF carries the same entity, and not merely the same shape.
 *
 * **This is the difference between stage 2 meaning something and not.** The
 * board's matching rule answers "did the extractor find the shape that is on the
 * page" — geometry within a tolerance, and for text the string and the anchor.
 * That is the right question for stage 1 and far too generous for stage 2,
 * because a DXF can carry every coordinate to the last decimal and still put the
 * text at the wrong height, turn it through ninety degrees, square off a rounded
 * corner, write it on another sheet's layer, or change its colour. Every one of
 * those is a file that does not carry what `drawing.json` says, and every one of
 * them scores a perfect round trip under the recognition rule alone.
 *
 * Applied as a *candidate* condition rather than reported separately, so a
 * mismatch lands in precision and recall like any other and needs no second
 * channel a reader could miss.
 */
function carriesTheSameEntity(readBack: DrawingEntity, declared: DrawingEntity): boolean {
  if (!sameLayer(readBack.layer, declared.layer)) return false;
  if (readBack.colour.toLowerCase() !== declared.colour.toLowerCase()) return false;

  // Stroke width is deliberately NOT compared. DXF lineweights are a fixed
  // enumeration in hundredths of a millimetre, so the writer snaps 0.27 mm to
  // 0.25 mm on purpose — a loss in the format rather than a defect in the file,
  // and comparing it strictly would fail every drawing whose pen widths are not
  // already legal DXF values.

  if (readBack.type !== declared.type) return false;

  if (readBack.type === 'TEXT' || readBack.type === 'MTEXT') {
    const d = declared as typeof readBack;
    if (Math.abs(readBack.height - d.height) > ROUND_TRIP_TOLERANCE_MM) return false;
    return angleGap(readBack.rotation, d.rotation) <= ROUND_TRIP_ANGLE_TOLERANCE_DEG;
  }

  if (readBack.type === 'LWPOLYLINE') {
    const d = declared as typeof readBack;
    // `closed` is already part of the board's rule; the bulges are not, and they
    // are the whole of the difference between a rounded rectangle and a sharp
    // one. Every bulge is zero today, so this compares nothing yet — which is
    // exactly why it has to be here before corner-arc recognition lands rather
    // than after.
    if (readBack.bulges.length !== d.bulges.length) return false;
    return readBack.bulges.every(
      (b, i) => Math.abs(b - (d.bulges[i] ?? 0)) <= ROUND_TRIP_TOLERANCE_MM,
    );
  }

  return true;
}

/**
 * A layer name survives the round trip, except that a multi-page document is
 * separated by layer because DXF has no pages: layer `0` on the second sheet is
 * written as `PAGE_2`, and layer `FRAME` as `PAGE_2_FRAME`.
 *
 * That prefix is a property of the DXF artefact rather than of this writer — it
 * is the only way the format can carry more than one sheet — so accepting it
 * here is reading the file's own convention, not agreeing with the code that
 * wrote it. Anything else is a mismatch.
 */
const PAGE_PREFIX = /^PAGE_\d+(?:_(.*))?$/;

function sameLayer(readBack: string, declared: string): boolean {
  if (readBack === declared) return true;
  const m = PAGE_PREFIX.exec(readBack);
  if (!m) return false;
  return (m[1] ?? '0') === declared;
}

export function scoreStages(input: StageInput): StageReports {
  const read = readDxf(input.dxf);
  const outlines = read.entities.filter((e) => isOutlineLayer(e.layer));
  const readBack = read.entities.filter((e) => !isOutlineLayer(e.layer));

  const exact = {
    toleranceMm: ROUND_TRIP_TOLERANCE_MM,
    arcAngleToleranceDeg: ROUND_TRIP_ANGLE_TOLERANCE_DEG,
    alsoRequire: carriesTheSameEntity,
  } as const;

  const board = input.toleranceMm === undefined ? {} : { toleranceMm: input.toleranceMm };

  return {
    stage1: scoreAgainstGroundTruth(input.produced, input.truth, board),
    stage2: scoreAgainstGroundTruth(readBack, input.produced, exact),
    endToEnd: scoreAgainstGroundTruth(readBack, input.truth, board),
    // The same rule stage 2 scored with, so the two cannot disagree about what
    // matched — only about how the leftovers are described.
    identity: verifyIdentity(input.produced, readBack, {
      toleranceMm: ROUND_TRIP_TOLERANCE_MM,
      arcAngleToleranceDeg: ROUND_TRIP_ANGLE_TOLERANCE_DEG,
      alsoRequire: carriesTheSameEntity,
      ...(input.pages === undefined ? {} : { pages: input.pages }),
    }),
    readBack: {
      entities: readBack.length,
      unsupported: read.unsupported,
      outlinesExcluded: outlines.length,
    },
  };
}
